import {
  Dialog as DialogRoot,
  DialogContent,
  DialogDescription,
  DialogPortal,
  DialogTitle,
  VisuallyHidden,
  cn,
} from "@mozioinc/ui";
import { describedByProps, includesComponent } from "../utils/react-children";
import type {
  DescriptionComponent,
  TitleComponent,
} from "../utils/react-children";
import type { ReactNode } from "react";

type DialogProps = {
  children:
    | ReactNode
    | ((Title: TitleComponent, Description: DescriptionComponent) => ReactNode);
  title?: ReactNode;
  description?: ReactNode;
  error?: boolean;
  success?: boolean;
  icon?: ReactNode;
  open?: boolean;
};

export const Dialog = ({
  icon,
  children,
  title,
  description,
  error,
  success,
  ...props
}: DialogProps) => {
  const content =
    typeof children === "function"
      ? children(DialogTitle, DialogDescription)
      : children;
  const describedBy =
    typeof children === "function"
      ? includesComponent(content, DialogDescription)
      : Boolean(description);

  return (
    <DialogRoot {...props}>
      <DialogPortal>
        <DialogContent
          className="flex max-w-md flex-col items-center justify-center bg-neutral-100 p-5 outline-none"
          {...describedByProps(describedBy)}
        >
          {icon && (
            <div
              className={cn(
                "-mt-24 flex h-32 w-32 items-center justify-center rounded-full border",
                {
                  "border-error-400 bg-error-400": error,
                  "border-success-300 bg-success-300": !error && success,
                  "border-neutral-200 bg-white": !error && !success,
                },
              )}
            >
              {icon}
            </div>
          )}
          {typeof children === "function" ? (
            content
          ) : (
            <>
              <VisuallyHidden asChild>
                <DialogTitle>{title}</DialogTitle>
              </VisuallyHidden>
              {description && (
                <VisuallyHidden asChild>
                  <DialogDescription>{description}</DialogDescription>
                </VisuallyHidden>
              )}
              {children}
            </>
          )}
        </DialogContent>
      </DialogPortal>
    </DialogRoot>
  );
};
